import './App.module.css';

import { Security } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { OktaConfig } from './data/appConfig';

import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import { ApiAuthBridge } from '@pella/fetch-client-react/okta';
import { AppProvider } from './providers';
import { MainLayout } from './components';
import { CameraProvider } from '@pella/barcode-scanner';

import { toast } from '@pella/sash-framework';

const oktaAuth = new OktaAuth(OktaConfig);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    /**
     * This is a global error handler for all queries
     * It will display a toast notification for any error that occurs
     * This is a good place to add global error handling for all queries
     * @see https://tkdodo.eu/blog/react-query-error-handling#the-global-callbacks
     * @param {Error} error
     */
    onError: (error) => {
      toast.dismiss();

      toast.error(error.message);
    },
  }),
  mutationCache: new MutationCache({
    /**
     * This is a global error handler for all mutations
     * It will display a toast notification for any error that occurs
     * This is a good place to add global error handling for all mutations
     * @see https://tkdodo.eu/blog/react-query-error-handling#the-global-callbacks
     * @param {Error} error
     */
    onError: (error) => {
      toast.dismiss();

      toast.error(error.message);
    },
  }),
});
const flagSmithApiKey =
  window.REACT_APP_FLAGSMITH_ENVIRONMENT_API_KEY ?? process.env.REACT_APP_FLAGSMITH_ENVIRONMENT_API_KEY;
/**
 * Structure taken straight from the Okta React React Router v6 sample
 * https://github.com/okta/okta-react/blob/master/samples/routing/react-router-dom-v6/src/App.tsx
 */
const App = () => {
  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <QueryClientProvider client={queryClient}>
        <ApiAuthBridge />
        <FlagsmithProvider flagsmith={flagsmith} options={{ environmentID: flagSmithApiKey, cacheFlags: true }}>
          <AppProvider>
            <CameraProvider>
              <MainLayout />
            </CameraProvider>
          </AppProvider>
        </FlagsmithProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Security>
  );
};

export default App;
